<template>
  <div id="ai-chatbox" :class="{ 'chat-is-fullscreen': isFullScreen }">
    <!-- Floating Trigger Button -->
    <vs-button
      v-if="!is_live && $can('view-ai-assistant-screen')"
      @click.stop="onClickOpenTaskSideBar"
      type="filled"
      class="side-btn magic-zap-btn"
      icon-pack="feather"
      icon="icon-zap"
    />

    <!-- Chat Sidebar -->
    <vs-sidebar
      click-not-close
      hidden-background
      position-right
      v-model="active"
      class="items-no-padding ai-chat-sidebar"
    >
      <div
        class="flex flex-col h-screen chat-container"
        :class="isDarkTheme ? 'theme-dark' : 'theme-light'"
      >
        <!-- Header -->
        <div class="flex justify-between items-center px-5 py-4 chat-header">
          <div class="flex items-center gap-3">
            <div class="ai-avatar-header flex items-center justify-center mr-2">
              <feather-icon icon="ZapIcon" svgClasses="w-5 h-5 text-white" />
            </div>
            <div>
              <div class="flex items-center">
                <h4 class="font-bold text-base leading-tight mb-0 mr-2">AI Assistant</h4>
                <span class="ai-badge-pill">Beta</span>
              </div>
              <p class="text-xs text-muted mb-0 mt-0.5">
                {{ session_name ? session_name : ('Hi ' + (currentUser.name || 'there')) }}
              </p>
            </div>
          </div>

          <div class="flex items-center">
            <vs-button
              v-if="session_id || messages.length"
              @click="createNewSession"
              icon-pack="feather"
              icon="icon-plus"
              size="small"
              type="border"
              color="primary"
              class="refresh-btn flex items-center justify-center mr-2"
              title="New Session"
            >
              New Chat
            </vs-button>

            <!-- Fullscreen Toggle Button -->
            <button
              type="button"
              class="header-icon-btn flex items-center justify-center mr-1"
              @click="toggleFullScreen"
              :title="isFullScreen ? 'Exit Full Screen' : 'Full Screen'"
            >
              <feather-icon :icon="isFullScreen ? 'Minimize2Icon' : 'Maximize2Icon'" svgClasses="w-4 h-4" />
            </button>

            <!-- Close Button -->
            <button
              type="button"
              class="close-btn flex items-center justify-center"
              @click.stop="active = false"
              title="Close Sidebar"
            >
              <feather-icon icon="XIcon" svgClasses="w-5 h-5" />
            </button>
          </div>
        </div>

        <!-- Auth Waiting State -->
        <div v-if="!is_logged_id" class="flex flex-col items-center justify-center flex-1 auth-wait">
          <div class="auth-spinner mb-3"></div>
          <span class="text-sm auth-text font-medium">
            Authenticating your session...
          </span>
        </div>

        <!-- Chat Main Panel -->
        <div v-else class="flex flex-col flex-1 min-h-0">
          <!-- Messages Scroll Area -->
          <div ref="chatBody" class="flex-1 min-h-0 overflow-y-auto p-4 space-y-4 chat-scroll">
            <!-- Welcome / Empty State -->
            <div v-if="messages.length === 0 && !isAiTyping" class="empty-chat-state text-center my-8">
              <div class="empty-chat-icon mx-auto mb-3 flex items-center justify-center">
                <feather-icon icon="ZapIcon" svgClasses="w-8 h-8 text-primary" />
              </div>
              <h5 class="font-semibold mb-1">How can I assist you today?</h5>
              <p class="text-xs text-muted max-w-xs mx-auto mb-4">
                Ask any question, query sales orders, analyze customer trends, or generate visual charts.
              </p>
              <div class="flex flex-wrap justify-center max-w-md mx-auto">
                <button
                  v-for="(prompt, pi) in starterPrompts"
                  :key="pi"
                  class="starter-chip text-xs"
                  @click="useSuggestion(prompt)"
                >
                  <feather-icon icon="SearchIcon" svgClasses="w-3 h-3 mr-1 inline" />
                  {{ prompt }}
                </button>
              </div>
            </div>

            <!-- Message List -->
            <div
              v-for="(msg, i) in messages"
              :key="i"
              class="flex flex-col mb-4"
              :class="msg.type === 'user' ? 'items-end' : 'items-start'"
            >
              <!-- USER MESSAGE -->
              <div v-if="msg.type === 'user'" class="flex items-end max-w-md">
                <div class="px-4 py-3 text-sm chat-user-bubble shadow-sm">
                  {{ msg.content }}
                </div>
              </div>

              <!-- AI MESSAGE -->
              <div v-if="msg.type === 'ai'" class="w-full flex items-start">
                <div class="ai-avatar-msg mr-3 flex-shrink-0">
                  <feather-icon icon="ZapIcon" svgClasses="w-4 h-4 text-white" />
                </div>

                <div class="flex-1 min-w-0 chat-ai-bubble shadow-sm">
                  <!-- Summary / Response Text -->
                  <div class="ai-summary font-medium text-sm mb-3 leading-relaxed">
                    {{ msg.summary }}
                  </div>

                  <!-- Visualization & Data Box -->
                  <div v-if="hasData(msg)" class="ai-data-card mb-4">
                    <!-- Top Bar: View Switcher (Chart/Table) + Column Selector Toggle -->
                    <div class="ai-data-card-header flex items-center justify-between flex-wrap px-3 py-2">
                      <!-- View Switcher -->
                      <div class="flex items-center">
                        <button
                          v-if="hasChart(msg)"
                          type="button"
                          class="view-tab-btn flex items-center text-xs mr-2"
                          :class="{ active: msg.activeView === 'chart' }"
                          @click="msg.activeView = 'chart'"
                        >
                          <feather-icon icon="BarChart2Icon" svgClasses="w-3.5 h-3.5 mr-1" />
                          <span>Chart</span>
                        </button>
                        <button
                          type="button"
                          class="view-tab-btn flex items-center text-xs"
                          :class="{ active: msg.activeView === 'table' }"
                          @click="msg.activeView = 'table'"
                        >
                          <feather-icon icon="GridIcon" svgClasses="w-3.5 h-3.5 mr-1" />
                          <span>Table</span>
                          <span v-if="msg.table && msg.table.length" class="text-muted ml-1">({{ msg.table.length }})</span>
                        </button>
                      </div>

                      <!-- Column Filter Dropdown Toggle -->
                      <div v-if="msg.available_columns && msg.available_columns.length" class="relative">
                        <button
                          type="button"
                          class="col-filter-btn flex items-center text-xs"
                          @click="msg.showColumnSelector = !msg.showColumnSelector"
                          :title="msg.showColumnSelector ? 'Hide Columns' : 'Filter Columns'"
                        >
                          <feather-icon icon="SlidersIcon" svgClasses="w-3.5 h-3.5 mr-1" />
                          <span class="mr-1">Columns</span>
                          <span class="col-count-pill mr-1">
                            {{ (msg.visible_columns ? msg.visible_columns.length : msg.available_columns.length) }}/{{ msg.available_columns.length }}
                          </span>
                          <feather-icon
                            :icon="msg.showColumnSelector ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                            svgClasses="w-3 h-3"
                          />
                        </button>

                        <!-- Column Toggle Popover Box -->
                        <div v-if="msg.showColumnSelector" class="col-selector-panel shadow-lg">
                          <div class="flex items-center justify-between px-3 py-2 border-b col-panel-header">
                            <span class="text-xs font-semibold">Display Columns</span>
                            <div class="flex items-center text-xs">
                              <span class="col-quick-action" @click="selectAllColumns(msg)">All</span>
                              <span class="text-muted mx-1.5">•</span>
                              <span class="col-quick-action" @click="resetColumns(msg)">Reset</span>
                            </div>
                          </div>
                          <div class="p-2 max-h-48 overflow-y-auto col-list-scroll">
                            <label
                              v-for="(col, ci) in msg.available_columns"
                              :key="ci"
                              class="col-checkbox-label flex items-center text-xs px-2 py-1 mb-1 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                :value="col"
                                v-model="msg.visible_columns"
                                class="mr-2 cursor-pointer"
                              />
                              <span class="truncate" :title="formatColumnName(col)">{{ formatColumnName(col) }}</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- VIEW 1: CHART VIEW -->
                    <div v-if="msg.activeView === 'chart' && hasChart(msg)" class="chart-wrapper p-3">
                      <vue-apex-charts
                        :type="getChartType(msg)"
                        height="280"
                        :options="getChartOptions(msg)"
                        :series="getChartSeries(msg)"
                      />
                    </div>

                    <!-- VIEW 2: TABLE VIEW -->
                    <div v-if="msg.activeView === 'table' || !hasChart(msg)" class="table-container">
                      <div class="table-scroll">
                        <table class="chat-table text-xs w-full">
                          <thead>
                            <tr>
                              <th
                                v-for="(col, ci) in getVisibleColumns(msg)"
                                :key="ci"
                                class="truncate"
                              >
                                {{ formatColumnName(col) }}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(row, ri) in msg.table" :key="ri">
                              <td
                                v-for="(col, ci) in getVisibleColumns(msg)"
                                :key="ci"
                                :class="{ 'text-muted-cell': isNullOrEmpty(row[col]) }"
                              >
                                <span v-if="isNullOrEmpty(row[col])" class="empty-cell">-</span>
                                <span v-else>{{ formatCellValue(row[col], col) }}</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <div class="table-footer px-3 py-2 flex items-center justify-between text-xs text-muted">
                        <span>{{ msg.table ? msg.table.length : 0 }} records</span>
                        <span>{{ getVisibleColumns(msg).length }} visible columns</span>
                      </div>
                    </div>
                  </div>

                  <!-- SQL Accordion with generous spacing -->
                  <div v-if="msg.sql" class="sql-section mt-3.5 mb-3">
                    <div
                      class="sql-toggle-bar flex items-center justify-between px-3 py-2 rounded cursor-pointer"
                      @click="msg.showSql = !msg.showSql"
                    >
                      <div class="flex items-center text-xs font-medium text-primary">
                        <feather-icon icon="CodeIcon" svgClasses="w-4 h-4 mr-2" />
                        <span>{{ msg.showSql ? "Hide SQL Query" : "View SQL Query" }}</span>
                      </div>
                      <feather-icon
                        :icon="msg.showSql ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                        svgClasses="w-3.5 h-3.5 text-primary"
                      />
                    </div>

                    <div v-if="msg.showSql" class="sql-box-wrapper relative mt-2 mb-2">
                      <button
                        type="button"
                        class="copy-sql-btn flex items-center text-xs"
                        @click="copySql(msg.sql, i)"
                        title="Copy SQL Query"
                      >
                        <feather-icon
                          :icon="copiedSqlIndex === i ? 'CheckIcon' : 'CopyIcon'"
                          svgClasses="w-3 h-3 mr-1"
                        />
                        <span>{{ copiedSqlIndex === i ? "Copied" : "Copy" }}</span>
                      </button>
                      <pre class="sql-code-box">{{ msg.sql }}</pre>
                    </div>
                  </div>

                  <!-- Action Suggestions with ample spacing -->
                  <div v-if="msg.suggestions && msg.suggestions.length" class="suggestions-section mt-4 pt-3 border-t">
                    <p class="text-xs text-muted mb-2 font-medium tracking-wide">Suggested follow-ups:</p>
                    <div class="flex flex-wrap suggestion-chip-container">
                      <button
                        v-for="(s, si) in msg.suggestions"
                        :key="si"
                        type="button"
                        class="suggestion-chip text-xs"
                        @click="useSuggestion(s)"
                      >
                        <feather-icon icon="ZapIcon" svgClasses="text-warning" />
                        <span>{{ s }}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- AI Thinking / Loading State -->
            <div v-if="isAiTyping" class="flex items-start mb-4">
              <div class="ai-avatar-msg mr-3 flex-shrink-0 animate-pulse">
                <feather-icon icon="ZapIcon" svgClasses="w-4 h-4 text-white" />
              </div>

              <div class="chat-ai-bubble thinking-card p-3.5 shadow-sm">
                <div class="flex items-center mb-1.5">
                  <div class="thinking-spinner mr-2"></div>
                  <span class="font-medium text-xs text-primary">AI is processing...</span>
                </div>
                <div class="text-xs thinking-step-text">
                  {{ thinkingSteps[currentThinkingStep] }}<span class="dots">{{ dots }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Input Footer -->
          <div class="chat-input-footer px-4 py-3 flex-shrink-0">
            <div class="chat-input-container flex items-center">
              <textarea
                ref="chatInput"
                v-model="newMessage"
                rows="1"
                @keydown.enter.exact.prevent="sendMessage"
                placeholder="Ask about orders, currencies, customers, charts..."
                class="flex-1 chat-textarea text-sm mr-2"
              ></textarea>

              <vs-button
                @click="sendMessage"
                :disabled="!newMessage.trim() || isAiTyping"
                icon-pack="feather"
                icon="icon-send"
                class="chat-send-btn flex items-center justify-center flex-shrink-0"
                type="filled"
                color="primary"
                title="Send Message"
              />
            </div>
            <div class="input-hint text-center mt-1.5">
              <span class="text-muted text-xs">Press <strong>Enter ↵</strong> to send, <strong>Shift+Enter</strong> for new line</span>
            </div>
          </div>
        </div>
      </div>
    </vs-sidebar>
  </div>
</template>

<script>
import store from "@/store/store.js";
import { RepositoryFactory } from "@/repository/RepositoryFactory";
const AiChatRepository = RepositoryFactory.get("ai_chat");

import Cookies from "js-cookie";
import ai_chat_axios from "../../ai_chat_axios";
import { is_live } from "@/constants/config";
import VueApexCharts from "vue-apexcharts";

export default {
  name: "aiChatBox",

  components: {
    VueApexCharts
  },

  data() {
    return {
      active: false,
      is_logged_id: false,
      isAiTyping: false,
      newMessage: "",
      currentUser: {},
      messages: [],
      session_id: null,
      session_name: null,
      copiedSqlIndex: null,
      isFullScreen: false,

      starterPrompts: [
        "Show latest 10 sales orders",
        "Customer count by currency",
        "Top 5 customers by sales",
        "Monthly orders breakdown"
      ],

      thinkingSteps: [
        "Analyzing your request...",
        "Generating database query...",
        "Connecting to system database...",
        "Executing query...",
        "Processing visualization & results...",
        "Preparing final response..."
      ],
      currentThinkingStep: 0,
      thinkingInterval: null,
      dotInterval: null,
      dots: "",
      is_live: is_live
    };
  },

  computed: {
    isDarkTheme() {
      return this.$store.state.theme !== "light";
    }
  },

  beforeDestroy() {
    this.stopThinkingAnimation();
  },

  methods: {
    toggleFullScreen() {
      this.isFullScreen = !this.isFullScreen;
      this.$nextTick(() => {
        window.dispatchEvent(new Event("resize"));
        this.scrollToBottom();
      });
    },

    async onClickOpenTaskSideBar() {
      this.active = !this.active;
      this.currentUser = store.getters.currentUser || {};
      this.$nextTick(() => {
        this.scrollToBottom();
        this.focusInput();
      });
      await this.checkAuthStatus();
    },

    async checkAuthStatus() {
      try {
        await AiChatRepository.getCurrentUer();
        this.is_logged_id = true;
      } catch (error) {
        this.is_logged_id = false;
        await this.exchangeToken();
      }
    },

    async exchangeToken() {
      try {
        const cache_user_token = Cookies.get("current_user_token");
        const { data } = await AiChatRepository.exchangeToken(cache_user_token);

        Cookies.set("ai_chat_token", data.access_token);
        ai_chat_axios.defaults.headers.common["Authorization"] =
          "Bearer " + data.access_token;

        await this.checkAuthStatus();
      } catch (error) {}
    },

    async sendMessage() {
      if (!this.newMessage.trim() || this.isAiTyping) return;

      const text = this.newMessage.trim();

      this.messages.push({
        type: "user",
        content: text
      });

      this.newMessage = "";
      this.isAiTyping = true;
      this.startThinkingAnimation();
      this.$nextTick(this.scrollToBottom);

      try {
        const payload = { message: text };
        if (this.session_id) {
          payload.session_id = this.session_id;
        }

        const { data } = await AiChatRepository.sendMessage(payload);

        if (data.session_id) {
          this.session_id = data.session_id;
        }
        if (data.session_name) {
          this.session_name = data.session_name;
        }

        let columns = [];
        if (data.viz && data.viz.options && data.viz.options.columns) {
          columns = data.viz.options.columns;
        } else if (data.data && data.data.length) {
          columns = Object.keys(data.data[0]);
        }

        const availableCols = data.available_columns || columns;
        const visibleCols = data.visible_columns || columns;

        const hasVizChart = data.viz && data.viz.kind && data.viz.kind !== "table";

        this.messages.push({
          type: "ai",
          summary: data.summary || "Here are the query results:",
          table: data.data || [],
          columns: columns,
          suggestions: data.suggestions || [],
          available_columns: availableCols,
          visible_columns: [...visibleCols],
          sql: data.sql || "",
          showSql: false,
          viz: data.viz || null,
          activeView: hasVizChart ? "chart" : "table",
          showColumnSelector: false
        });
      } catch (error) {
        this.messages.push({
          type: "ai",
          summary: "Sorry, something went wrong while processing your request. Please try again.",
          table: [],
          columns: [],
          suggestions: [],
          available_columns: [],
          visible_columns: [],
          sql: "",
          showSql: false,
          viz: null,
          activeView: "table",
          showColumnSelector: false
        });
      }

      this.stopThinkingAnimation();
      this.isAiTyping = false;
      this.$nextTick(() => {
        this.scrollToBottom();
        this.focusInput();
      });
    },

    startThinkingAnimation() {
      this.currentThinkingStep = 0;
      this.dots = "";

      this.thinkingInterval = setInterval(() => {
        if (this.currentThinkingStep < this.thinkingSteps.length - 1) {
          this.currentThinkingStep++;
        }
      }, 1800);

      this.dotInterval = setInterval(() => {
        if (this.dots.length >= 3) {
          this.dots = "";
        } else {
          this.dots += ".";
        }
      }, 400);
    },

    stopThinkingAnimation() {
      if (this.thinkingInterval) {
        clearInterval(this.thinkingInterval);
        this.thinkingInterval = null;
      }
      if (this.dotInterval) {
        clearInterval(this.dotInterval);
        this.dotInterval = null;
      }
      this.dots = "";
    },

    useSuggestion(text) {
      this.newMessage = text;
      this.sendMessage();
    },

    createNewSession() {
      this.session_id = null;
      this.session_name = null;
      this.messages = [];
      this.focusInput();
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatBody;
        if (!el) return;
        el.scrollTop = el.scrollHeight + 300;
      });
    },

    focusInput() {
      this.$nextTick(() => {
        if (this.$refs.chatInput) {
          this.$refs.chatInput.focus();
        }
      });
    },

    hasData(msg) {
      return (msg.table && msg.table.length > 0) || (msg.viz && msg.viz.kind);
    },

    hasChart(msg) {
      if (!msg.viz || !msg.viz.kind || !msg.table || !msg.table.length) return false;
      const kind = msg.viz.kind.toLowerCase();
      return kind !== "table";
    },

    getChartType(msg) {
      if (!msg.viz || !msg.viz.kind) return "bar";
      const kind = msg.viz.kind.toLowerCase();
      if (kind.includes("bar")) return "bar";
      if (kind.includes("line")) return "line";
      if (kind.includes("area")) return "area";
      if (kind.includes("pie")) return "pie";
      if (kind.includes("donut")) return "donut";
      return "bar";
    },

    getChartSeries(msg) {
      if (!msg.table || !msg.table.length || !msg.viz) return [];
      const viz = msg.viz;
      const kind = this.getChartType(msg);

      if (kind === "pie" || kind === "donut") {
        const yField = viz.y && viz.y[0] ? viz.y[0].field : (viz.options && viz.options.columns ? viz.options.columns[1] : null);
        if (!yField) return [];
        return msg.table.map(row => Number(row[yField]) || 0);
      }

      if (Array.isArray(viz.y) && viz.y.length > 0) {
        return viz.y.map(yObj => ({
          name: yObj.label || this.formatColumnName(yObj.field),
          data: msg.table.map(row => {
            const val = row[yObj.field];
            return val !== null && val !== undefined && !isNaN(Number(val)) ? Number(val) : 0;
          })
        }));
      }

      if (viz.y && viz.y.field) {
        return [{
          name: viz.y.label || this.formatColumnName(viz.y.field),
          data: msg.table.map(row => Number(row[viz.y.field]) || 0)
        }];
      }

      // Fallback: look for numeric columns
      const cols = msg.columns || Object.keys(msg.table[0] || {});
      const numCol = cols.find(c => msg.table.some(r => typeof r[c] === "number"));
      if (numCol) {
        return [{
          name: this.formatColumnName(numCol),
          data: msg.table.map(row => Number(row[numCol]) || 0)
        }];
      }

      return [];
    },

    getChartOptions(msg) {
      if (!msg.table || !msg.table.length || !msg.viz) return {};
      const viz = msg.viz;
      const isDark = this.isDarkTheme;
      const kind = this.getChartType(msg);

      let xField = viz.x ? viz.x.field : null;
      if (!xField && viz.options && viz.options.columns && viz.options.columns.length > 0) {
        xField = viz.options.columns[0];
      }
      if (!xField && msg.table.length > 0) {
        xField = Object.keys(msg.table[0])[0];
      }

      const categories = msg.table.map(row => {
        const val = row[xField];
        return val !== null && val !== undefined ? String(val) : "-";
      });

      const colors = ["#7367F0", "#28C76F", "#FF9F43", "#00CFE8", "#EA5455", "#9C27B0", "#009688", "#3F51B5"];

      if (kind === "pie" || kind === "donut") {
        return {
          chart: {
            type: kind,
            fontFamily: "Montserrat, Helvetica, Arial, sans-serif",
            background: "transparent",
            toolbar: { show: true }
          },
          labels: categories,
          colors: colors,
          theme: { mode: isDark ? "dark" : "light" },
          legend: {
            position: "bottom",
            labels: { colors: isDark ? "#c2c6dc" : "#626262" }
          },
          tooltip: { theme: isDark ? "dark" : "light" },
          responsive: [{
            breakpoint: 480,
            options: {
              chart: { width: "100%" },
              legend: { position: "bottom" }
            }
          }]
        };
      }

      const yLabel = viz.y && Array.isArray(viz.y) && viz.y[0]
        ? (viz.y[0].label || this.formatColumnName(viz.y[0].field))
        : "";

      return {
        chart: {
          type: kind,
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false
            }
          },
          fontFamily: "Montserrat, Helvetica, Arial, sans-serif",
          background: "transparent",
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 400
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "45%",
            borderRadius: 4
          }
        },
        dataLabels: { enabled: false },
        stroke: {
          show: true,
          width: kind === "bar" ? 0 : 2.5,
          curve: "smooth"
        },
        colors: colors,
        xaxis: {
          categories: categories,
          title: {
            text: viz.x && viz.x.label ? viz.x.label : this.formatColumnName(xField),
            style: {
              color: isDark ? "#c2c6dc" : "#626262",
              fontSize: "11px",
              fontWeight: 600
            }
          },
          labels: {
            rotate: -35,
            rotateAlways: categories.length > 5,
            style: {
              colors: isDark ? "#c2c6dc" : "#626262",
              fontSize: "10px"
            }
          },
          axisBorder: { color: isDark ? "#414561" : "#dae1e7" },
          axisTicks: { color: isDark ? "#414561" : "#dae1e7" }
        },
        yaxis: {
          title: {
            text: yLabel,
            style: {
              color: isDark ? "#c2c6dc" : "#626262",
              fontSize: "11px",
              fontWeight: 600
            }
          },
          labels: {
            style: {
              color: isDark ? "#c2c6dc" : "#626262",
              fontSize: "10px"
            }
          }
        },
        grid: {
          borderColor: isDark ? "#414561" : "#ebe9f1",
          strokeDashArray: 3,
          padding: { top: 0, right: 10, bottom: 0, left: 10 }
        },
        tooltip: {
          theme: isDark ? "dark" : "light"
        },
        theme: {
          mode: isDark ? "dark" : "light"
        }
      };
    },

    getVisibleColumns(msg) {
      if (msg.visible_columns && msg.visible_columns.length > 0) {
        return msg.visible_columns;
      }
      if (msg.available_columns && msg.available_columns.length > 0) {
        return msg.available_columns;
      }
      if (msg.columns && msg.columns.length > 0) {
        return msg.columns;
      }
      if (msg.table && msg.table.length > 0) {
        return Object.keys(msg.table[0]);
      }
      return [];
    },

    formatColumnName(col) {
      if (!col) return "";

      const upperAcronyms = {
        id: "ID",
        fx: "FX",
        sql: "SQL",
        crm: "CRM",
        so: "SO",
        po: "PO",
        sku: "SKU",
        vat: "VAT",
        usd: "USD",
        gbp: "GBP",
        eur: "EUR",
        aud: "AUD",
        dkk: "DKK",
        qty: "Qty",
        eod: "EOD",
        url: "URL"
      };

      // Split by underscores, dashes, or camelCase boundaries
      const cleaned = col.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ");

      return cleaned
        .split(" ")
        .filter(Boolean)
        .map(word => {
          const lower = word.toLowerCase();
          if (upperAcronyms[lower]) {
            return upperAcronyms[lower];
          }
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(" ");
    },

    formatCellValue(val, col) {
      if (this.isNullOrEmpty(val)) return "-";
      if (typeof val === "boolean") return val ? "Yes" : "No";

      // If it's a number and col suggests currency / fx
      if (typeof val === "number" && (col.includes("fx_value") || col.includes("price") || col.includes("amount") || col.includes("cost"))) {
        return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
      }

      return String(val);
    },

    isNullOrEmpty(val) {
      return val === null || val === undefined || val === "";
    },

    selectAllColumns(msg) {
      if (msg.available_columns) {
        msg.visible_columns = [...msg.available_columns];
      }
    },

    resetColumns(msg) {
      if (msg.columns && msg.columns.length) {
        msg.visible_columns = [...msg.columns];
      } else if (msg.available_columns) {
        msg.visible_columns = [...msg.available_columns];
      }
    },

    async copySql(sql, index) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(sql);
        } else if (this.$copyText) {
          await this.$copyText(sql);
        }
        this.copiedSqlIndex = index;
        setTimeout(() => {
          if (this.copiedSqlIndex === index) {
            this.copiedSqlIndex = null;
          }
        }, 2500);

        this.$vs.notify({
          title: "Copied",
          text: "SQL query copied to clipboard",
          color: "success",
          iconPack: "feather",
          icon: "icon-check",
          position: "top-right"
        });
      } catch (e) {
        // clipboard fallback
      }
    }
  }
};
</script>

<style scoped lang="scss">
#ai-chatbox ::v-deep .vs-sidebar {
  position: fixed !important;
  top: 0;
  right: 0;
  height: 100vh;
  width: 820px;
  max-width: 95vw;
  z-index: 999999;
  border-radius: 18px 0 0 18px;
  overflow: hidden;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.18);
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.3s ease;
}

#ai-chatbox.chat-is-fullscreen ::v-deep .vs-sidebar {
  width: 100vw !important;
  max-width: 100vw !important;
  border-radius: 0 !important;
}

.header-icon-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  color: #626262;
  transition: all 0.2s;

  &:hover {
    background: rgba(115, 103, 240, 0.1);
    color: #7367F0;
  }
}

/* Base Light Theme */
.theme-light {
  background-color: #f8f8f8;
  color: #2c2c2c;

  .chat-header {
    background: #ffffff;
    border-bottom: 1px solid #ebe9f1;
  }

  .chat-ai-bubble {
    background: #ffffff;
    border: 1px solid #ebe9f1;
    color: #2c2c2c;
  }

  .ai-data-card {
    background: #fafbfd;
    border: 1px solid #e7ebf0;
  }

  .ai-data-card-header {
    background: #f1f4f9;
    border-bottom: 1px solid #e7ebf0;
  }

  .chat-table th {
    background: #f8fafc;
    color: #5e5873;
    border-bottom: 1px solid #ebe9f1;
  }

  .chat-table td {
    border-bottom: 1px solid #f1f3f5;
    color: #2c2c2c;
  }

  .chat-table tr:hover td {
    background: #f4f6fb;
  }

  .table-footer {
    background: #f8fafc;
    border-top: 1px solid #ebe9f1;
  }

  .col-selector-panel {
    background: #ffffff;
    border: 1px solid #ebe9f1;
  }

  .col-checkbox-label:hover {
    background: #f3f4f8;
  }

  .sql-toggle-bar {
    background: #edf2f9;
    border: 1px solid #dae3f0;
  }

  .sql-toggle-bar:hover {
    background: #e3ebf6;
    border-color: #cbd8ec;
  }

  .suggestions-section {
    border-top-color: #ebe9f1;
  }

  .chat-input-footer {
    background: #ffffff;
    border-top: 1px solid #ebe9f1;
  }

  .chat-input-container {
    background: #f8f8f8;
    border: 1px solid #dae1e7;
  }
}

/* Dark Theme */
.theme-dark {
  background-color: #10163a;
  color: #c2c6dc;

  .chat-header {
    background: #262c49;
    border-bottom: 1px solid #414561;
    color: #ebeefd;

    .header-icon-btn {
      color: #c2c6dc;

      &:hover {
        background: rgba(255, 255, 255, 0.08);
        color: #7367F0;
      }
    }
  }

  .chat-ai-bubble {
    background: #262c49;
    border: 1px solid #414561;
    color: #c2c6dc;
  }

  .ai-data-card {
    background: #171e49;
    border: 1px solid #414561;
  }

  .ai-data-card-header {
    background: #10163a;
    border-bottom: 1px solid #414561;
  }

  .chat-table th {
    background: #10163a;
    color: #ebeefd;
    border-bottom: 1px solid #414561;
  }

  .chat-table td {
    border-bottom: 1px solid #343b60;
    color: #c2c6dc;
  }

  .chat-table tr:hover td {
    background: #1f2754;
  }

  .table-footer {
    background: #10163a;
    border-top: 1px solid #414561;
  }

  .col-selector-panel {
    background: #262c49;
    border: 1px solid #414561;
    color: #c2c6dc;
  }

  .col-panel-header {
    border-bottom: 1px solid #414561 !important;
  }

  .col-checkbox-label:hover {
    background: #10163a;
  }

  .col-checkbox-label span {
    color: #c2c6dc;
  }

  .sql-toggle-bar {
    background: #10163a;
    border: 1px solid #2f365f;
  }

  .sql-toggle-bar:hover {
    background: #18204d;
    border-color: #414561;
  }

  .suggestions-section {
    border-top-color: #414561;
  }

  .chat-input-footer {
    background: #262c49;
    border-top: 1px solid #414561;
  }

  .chat-input-container {
    background: #10163a;
    border: 1px solid #414561;
  }

  .chat-textarea {
    color: #ebeefd;
  }

  .empty-chat-icon {
    background: #262c49;
  }

  .starter-chip {
    background: #262c49;
    border-color: #414561;
    color: #c2c6dc;

    &:hover {
      background: #7367f0;
      color: #ffffff;
      border-color: #7367f0;
    }
  }

  .suggestion-chip {
    background: #10163a;
    border-color: #383f68;
    color: #c2c6dc;

    &:hover {
      background: #7367f0;
      color: #ffffff;
      border-color: #7367f0;
    }
  }
}

/* Header Avatar */
.ai-avatar-header {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #7367F0, #9E95F5);
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.4);
}

.ai-badge-pill {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  background: rgba(115, 103, 240, 0.15);
  color: #7367F0;
  padding: 2px 6px;
  border-radius: 6px;
}

.close-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  color: #626262;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #ea5455;
  }
}

/* Chat Scroll & Area */
.chat-scroll {
  height: 100%;
  overflow-y: auto;
}

.chat-scroll::-webkit-scrollbar {
  width: 5px;
}

.chat-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
  border-radius: 10px;
}

/* Message Bubbles */
.chat-user-bubble {
  background: linear-gradient(135deg, #7367F0, #8A7FF5);
  color: #ffffff;
  border-radius: 16px 16px 4px 16px;
  line-height: 1.45;
  word-break: break-word;
}

.ai-avatar-msg {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7367F0, #9E95F5);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(115, 103, 240, 0.3);
}

.chat-ai-bubble {
  border-radius: 4px 16px 16px 16px;
  padding: 16px 18px;
  transition: all 0.2s;
  overflow: hidden;
}

/* Data Card & Visualizations */
.ai-data-card {
  border-radius: 10px;
  overflow: hidden;
  margin-top: 8px;
}

.view-tab-btn {
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: #626262;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background: rgba(115, 103, 240, 0.08);
    color: #7367F0;
  }

  &.active {
    background: #7367F0;
    color: #ffffff;
    box-shadow: 0 2px 6px rgba(115, 103, 240, 0.3);
  }
}

.col-filter-btn {
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid #dae1e7;
  background: transparent;
  color: #626262;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    border-color: #7367F0;
    color: #7367F0;
  }
}

.col-count-pill {
  font-size: 10px;
  background: rgba(115, 103, 240, 0.12);
  color: #7367F0;
  padding: 1px 5px;
  border-radius: 4px;
  font-weight: 600;
}

/* Column Popover */
.col-selector-panel {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 6px;
  width: 220px;
  border-radius: 8px;
  z-index: 100;
}

.col-quick-action {
  color: #7367F0;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
}

.col-list-scroll::-webkit-scrollbar {
  width: 4px;
}

.col-list-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

/* Table */
.table-container {
  width: 100%;
}

.table-scroll {
  width: 100%;
  overflow-x: auto;
  max-height: 380px;
}

.table-scroll::-webkit-scrollbar {
  height: 6px;
  width: 6px;
}

.table-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
}

.chat-table {
  border-collapse: collapse;
  min-width: 100%;
  white-space: nowrap;
}

.chat-table th {
  padding: 9px 14px;
  font-weight: 600;
  text-align: left;
  position: sticky;
  top: 0;
  z-index: 10;
}

.chat-table td {
  padding: 8px 14px;
  font-size: 11px;
}

.text-muted-cell {
  opacity: 0.6;
}

.empty-cell {
  font-style: italic;
  color: #a0aec0;
}

/* SQL Accordion */
.sql-section {
  width: 100%;
}

.sql-toggle-bar {
  transition: all 0.2s;
  user-select: none;
}

.sql-box-wrapper {
  border-radius: 8px;
  overflow: hidden;
}

.copy-sql-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  padding: 4px 10px;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
}

.sql-code-box {
  margin: 0;
  background: #0d1326;
  color: #79f2c0;
  padding: 14px 16px;
  border-radius: 8px;
  font-size: 11px;
  line-height: 1.5;
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
}

/* Suggestions Container & Chips */
.suggestions-section {
  width: 100%;
}

.suggestion-chip-container {
  margin-top: 4px;
}

.suggestion-chip {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  margin-right: 8px;
  margin-bottom: 8px;
  border-radius: 20px;
  border: 1px solid #dae1e7;
  background: #ffffff;
  color: #5e5873;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
  line-height: 1.3;

  ::v-deep .feather-icon {
    display: inline-flex;
    align-items: center;
    margin-right: 6px;

    svg {
      width: 12px !important;
      height: 12px !important;
      stroke-width: 2.2;
    }
  }

  &:hover {
    border-color: #7367F0;
    color: #7367F0;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(115, 103, 240, 0.18);
  }
}

.starter-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  margin: 4px;
  border-radius: 18px;
  border: 1px solid #dae1e7;
  background: #ffffff;
  color: #5e5873;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;

  ::v-deep .feather-icon {
    display: inline-flex;
    align-items: center;
    margin-right: 6px;

    svg {
      width: 12px !important;
      height: 12px !important;
    }
  }

  &:hover {
    background: #7367F0;
    color: #ffffff;
    border-color: #7367F0;
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(115, 103, 240, 0.25);
  }
}

.empty-chat-icon {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: #f0f0ff;
}

/* Thinking Indicator */
.thinking-card {
  border-radius: 4px 16px 16px 16px;
  min-width: 220px;
}

.thinking-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(115, 103, 240, 0.3);
  border-top-color: #7367F0;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.thinking-step-text {
  font-style: italic;
  opacity: 0.85;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Input Area */
.chat-input-container {
  border-radius: 12px;
  padding: 6px 10px;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: #7367F0 !important;
    box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.15);
  }
}

.chat-textarea {
  background: transparent;
  border: none;
  outline: none;
  resize: none;
  padding: 6px 4px;
  font-family: inherit;
  line-height: 1.4;

  &::placeholder {
    color: #a0aec0;
  }
}

.chat-send-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px !important;
  padding: 0 !important;
  transition: transform 0.15s;

  &:hover:not(:disabled) {
    transform: scale(1.05);
  }
}

/* Trigger Zap Button */
.side-btn {
  position: fixed;
  top: 15%;
  right: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  z-index: 50000;
}

.magic-zap-btn {
  background: linear-gradient(135deg, #FFD54F, #FFB300) !important;
  border-color: #FFB300 !important;
  color: #fff !important;
  box-shadow: 0 0 12px rgba(255, 193, 7, 0.7);
  animation: zapGlow 2.5s infinite;

  &:hover {
    background: linear-gradient(135deg, #FFE082, #FFC107) !important;
    border-color: #FFC107 !important;
    box-shadow: 0 0 18px rgba(255, 193, 7, 0.9);
    transform: translateY(-1px);
  }

  i {
    font-size: 18px;
  }
}

@keyframes zapGlow {
  0%, 100% {
    box-shadow: 0 0 8px rgba(255, 193, 7, 0.4);
  }
  50% {
    box-shadow: 0 0 18px rgba(255, 193, 7, 0.9);
  }
}

/* Auth Spinner */
.auth-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(115, 103, 240, 0.2);
  border-top-color: #7367F0;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
</style>